import React, { useLayoutEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import { useHashView } from './useHashView';
import { clearHashAuth } from './redeemHash';
import { savePostLoginRedirect } from 'global/utils/loginRedirect';
import { buildShareUrl, IN_SCOPE_PATH_FOR } from './shareLinkConfig';
import { HASH_BANNER_HEIGHT_VAR } from 'global/components/FlexibleLayout/constants';

const Banner = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  flexShrink: 0,
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.getContrastText(theme.palette.warning.light),
  padding: theme.spacing(1, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(1),
  },
  '@media print': {
    display: 'none',
  },
}));

const BannerText = styled('div')(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1rem',
  lineHeight: 1.35,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
  },
}));

const SubText = styled('div')(({ theme }) => ({
  fontStyle: 'italic',
  fontSize: '0.95rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const BannerButton = styled(Button)(({ theme }) => ({
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
    width: '100%',
  },
}));

// Friendly display of an expiry value. Backend may send a date-only string or
// an ISO datetime — we parse loosely and fall back to the raw string.
const formatExpiry = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

const contentNoun = (kind) => {
  switch (kind) {
    case 'assessment':
      return 'an assessment';
    case 'task':
      return 'a task';
    case 'document':
      return 'a document';
    default:
      return 'content';
  }
};

const clearBannerHeightVar = () => {
  document.documentElement.style.removeProperty(HASH_BANNER_HEIGHT_VAR);
};

const publishBannerHeight = (el) => {
  if (!el) return;
  document.documentElement.style.setProperty(HASH_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
};

export default function HashAccessBanner() {
  const dispatch = useDispatch();
  const history = useHistory();
  const bannerRef = useRef(null);
  const { active, hash, scope, expiresAt, loggedIn } = useHashView();

  // Publish live height so DocumentSoloShell can size under a wrapping banner.
  useLayoutEffect(() => {
    if (!active) {
      clearBannerHeightVar();
      return undefined;
    }

    const el = bannerRef.current;
    if (!el) return undefined;

    publishBannerHeight(el);
    const observer = new ResizeObserver(() => publishBannerHeight(el));
    observer.observe(el);

    return () => {
      observer.disconnect();
      clearBannerHeightVar();
    };
  }, [active]);

  if (!active) return null;

  const expiry = formatExpiry(expiresAt);
  const noun = contentNoun(scope?.kind);

  const handleLogin = () => {
    // After login, return the user to the ORIGINAL /share/:hash URL rather than the
    // canonical content path. The hash session does not survive the login reload
    // (hashAuth is in the redux-persist blacklist), so routing through the share URL
    // lets ShareRedemptionRoute re-redeem the hash and re-establish the session.
    // This keeps a user WITHOUT normal access on the shared content (with the
    // "you don't normally have access" banner) instead of bouncing them to the list,
    // and lets a user WITH normal access fall through to the canonical view (PFM-2636).
    const shareUrl = buildShareUrl(hash, scope);
    const target = shareUrl || IN_SCOPE_PATH_FOR(scope);
    savePostLoginRedirect({ pathname: target, search: '', hash: '' });
    dispatch(clearHashAuth());
    history.push('/');
  };

  const handleGoToPerform = () => {
    dispatch(clearHashAuth());
    history.push('/home');
  };

  return (
    <Banner ref={bannerRef} data-testid="hash-access-banner">
      <BannerText>
        <>
          You are viewing {noun} shared via a link that{' '}
          <strong>expires on {expiry}</strong>.
        </>
        {loggedIn ? (
          <SubText>Your profile does not have access to this content normally</SubText>
        ) : (
          <SubText>Login to see more details</SubText>
        )}
      </BannerText>
      {loggedIn ? (
        <BannerButton
          variant="contained"
          color="primary"
          onClick={handleGoToPerform}
          data-testid="hash-banner-cta"
        >
          Go to PERFORM
        </BannerButton>
      ) : (
        <BannerButton
          variant="contained"
          color="primary"
          startIcon={<LoginIcon />}
          onClick={handleLogin}
          data-testid="hash-banner-cta"
        >
          Login
        </BannerButton>
      )}
    </Banner>
  );
}
